import React, { createContext, useContext, useMemo, useState } from 'react'

import { GanttContext } from '@/shared/components/GanttChart/GanttContext'

export type OperationsGanttContextType = {
  widgetId: `GanttChart-${string}`
  setWidgetId: (widgetId: `GanttChart-${string}`) => void
}

export const OperationsGanttContext = createContext<OperationsGanttContextType>({
  widgetId: 'GanttChart-',
  setWidgetId: () => { },
})

export const useOperationsGanttContext = () => {
  const context = useContext(OperationsGanttContext)

  if (context === undefined) {
    throw new Error(
      'useOperationsGanttContext must be used within a OperationsGanttWidgetContextProvider',
    )
  }

  return context
}

/**
 * The OperationsGanttWidgetContextProvider is a context provider that help share the state
 *    between the OperationsGanttWidget and the OperationsGanttWidgetToolbar components through
 *    the Mosaic Window rendering. Thus, any local actions or state that need to be shared
 *    between these two components should be managed here.
 */
export type OperationsGanttWidgetContextProps = {
  children?: React.ReactNode
}

export const OperationsGanttWidgetContextProvider: React.FC<OperationsGanttWidgetContextProps> = ({ children }) => {
  const [widgetId, setWidgetId] = useState<`GanttChart-${string}`>('GanttChart-')
  const [zoom, setZoom] = useState<number>(2)

  const contextValue = useMemo(() => {
    return {
      widgetId,
      setWidgetId,
    }
  }, [widgetId])

  const ganttContextValue = useMemo(() => {
    return {
      zoom,
      setZoom,
      defaultZoom: 2,
    }
  }, [zoom])

  return (
    <OperationsGanttContext.Provider value={contextValue}>
      <GanttContext.Provider value={ganttContextValue}>
        {children}
      </GanttContext.Provider>
    </OperationsGanttContext.Provider>
  )
}
